/**
 * What each make on `/collections` is actually stocked for.
 *
 * The index page can say a make's name and how many parts carry it, but the
 * question a customer is really asking is narrower: do you do WIPERS for a
 * Toyota? Measured over the live index, 23 of 41 makes carry only one of the
 * two categories — 21 deflectors-only, 2 wipers-only — so for more than half
 * the list the honest answer is "no", and finding that out costs a tap and a
 * page load. This puts it on the row instead.
 *
 * Read from `fitment-index.json`, which is the same file the finder, the tiles
 * and the 404's guesser already use. That is the point: the coverage shown here
 * is derived from the very data a search resolves against, so the two cannot
 * drift the way a hand-maintained label or a collection metafield would. The
 * copy came out of the same source and is measured, not asserted.
 *
 * Enrichment only. The row is a name, a count and a link before this runs, and
 * stays one if the index never arrives — no spinner, no error state, nothing
 * for the reader to do about it.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-af-make-index]');
  if (!root) return;

  const rows = root.querySelectorAll('[data-af-make]');
  if (!rows.length) return;

  const INDEX_KEY = 'airflux:index';

  /**
   * Comparison key for a make — accents FOLDED, not dropped.
   *
   * 🔴 The same rule as `makeKey` in `airflux-fitment.js`, and it matters for
   * exactly the same two makes: the collection titles are "Citroën" and
   * "Škoda". Stripping non-alphanumerics without folding first turns those into
   * "citron" and "koda", which match nothing in the index — so the two makes
   * would silently be the only ones on the page with no coverage line, and it
   * would read as missing data rather than a bug.
   */
  const makeKey = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]/g, '');

  /**
   * The index, preferring the copy `airflux-fitment.js` parks in sessionStorage
   * under the same cache-busted asset URL. The closing CTA's finder boots on
   * this page too, so in practice that copy is already there and this costs
   * nothing; the fetch is the fallback for a browser that refused the write.
   */
  const loadIndex = async (url) => {
    try {
      const cached = sessionStorage.getItem(`${INDEX_KEY}:${url}`);
      if (cached) return JSON.parse(cached);
    } catch {
      /* unreadable cache — fall through to the network */
    }
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  (async () => {
    let index;
    try {
      index = await loadIndex(root.dataset.indexUrl);
    } catch {
      return;
    }

    // One pass over the vehicles: which categories does each make carry at all?
    const coverage = new Map();
    for (const v of index.vehicles) {
      const key = makeKey(index.makes[v.m]);
      if (!key) continue;
      let entry = coverage.get(key);
      if (!entry) coverage.set(key, (entry = { d: false, w: false }));
      if (v.d?.length) entry.d = true;
      if (v.w?.length) entry.w = true;
    }

    const labels = {
      both: root.dataset.labelBoth,
      d: root.dataset.labelDeflectors,
      w: root.dataset.labelWipers,
    };

    for (const row of rows) {
      const slot = row.querySelector('[data-af-make-cats]');
      if (!slot) continue;

      const entry = coverage.get(makeKey(row.dataset.afMake));
      // A make with neither is a collection the index does not know about.
      // Say nothing rather than "no categories" — the count beside it is
      // already the honest signal, and the row still works as a link.
      if (!entry || (!entry.d && !entry.w)) continue;

      const label = entry.d && entry.w ? labels.both : entry.d ? labels.d : labels.w;
      if (label) slot.textContent = label;
    }
  })();
})();
